import { RecordingFolderGridDataSource } from './RecordingFolderGridDataSource';
import {
  DetailTranslationKeys,
  PopupTranslationKeys,
  RecordingTranslationKeys,
  SettingsKeys,
} from '../../api/models/TranslationKeys';
import { CmsProperty } from '../../api/models/Shared.types';
import { confirmCancelDialog } from '../../pages/dialogs/ConfirmCancelDialog';
import { Recording, RecordingPageTypes } from '../../api/user/RecordingApi.types';
import { RecordingPage } from '../../pages/content/recording/RecordingPage';
import { mapRecordingPageType } from '../utility/PageTypeUtil';
import { Backend } from '../../api/Backend';
import { ChannelModel } from '../../api/EpgApi.types';
import { Lightning, Log } from '@lightningjs/sdk';
import { EpgApiV5 } from '../../api/EpgApiV5';

export interface SeasonRecordings {
  season?: number;
  id?: string;
  recordings: Recording[];
}

export interface FolderInfo {
  seriesId: string;
  channelId: string;
  pageType: RecordingPageTypes;
}

export class RecordingFolderDataSource {
  private backend: Backend;
  private recordings: Recording[];
  private channels: ChannelModel[];
  private folderInfo: FolderInfo;
  private owner: Lightning.Component;

  constructor(
    backend: Backend,
    owner: Lightning.Component,
    recordings: Recording[],
    channels: ChannelModel[],
    folderInfo: FolderInfo
  ) {
    this.backend = backend;
    this.owner = owner;
    this.recordings = recordings;
    this.channels = channels;
    this.folderInfo = folderInfo;
  }

  /**
   * @returns {Promise<GridDataSource>[]}
   */
  getRecordingContent() {
    const grouped = this._groupBySeason(this.recordings);
    const grids: Promise<RecordingFolderGridDataSource>[] = [];
    grouped.forEach(group => {
      grids.push(
        this._gridTitle(group).then(title => {
          const grid = new RecordingFolderGridDataSource(
            this.backend,
            title,
            group.recordings,
            this.channels,
            this.pageProperties as CmsProperty[]
          );
          return grid.load();
        })
      );
    });
    return grids;
  }

  /**
   * @param {Recording[]} recordings
   * @returns {SeasonRecordings[]}
   * @private
   */
  _groupBySeason(recordings: Recording[]): SeasonRecordings[] {
    if (recordings.length === 0) {
      return [];
    }
    /**
     * @type {SeasonRecordings[]}
     */
    const seasons: SeasonRecordings[] = [];
    recordings.forEach(rec => {
      let seasonIndex = seasons.findIndex(season => season.id === rec.season);
      if (seasonIndex === -1) {
        seasonIndex =
          seasons.push({
            season: rec.seasonOrdinal,
            id: rec.season,
            recordings: [],
          }) - 1;
      }
      seasons[seasonIndex]?.recordings.push(rec);
    });

    seasons.sort((a, b) => {
      if (a.season !== undefined && b.season !== undefined) {
        return a.season - b.season;
      } else if (a.season !== undefined) {
        return 1;
      } else {
        return -1;
      }
    });
    return seasons;
  }

  get pageProperties(): string[] {
    let properties;
    properties = this.backend.globalSettings.stringArray(SettingsKeys.propertiesRecordingEpisodesRibbon);
    properties = properties
      ? properties
      : [
          CmsProperty.ShowProgress,
          CmsProperty.UseBestSchedule,
          CmsProperty.ShowScheduleInfo,
          CmsProperty.ShowEpisodeInfo,
        ];
    return properties;
  }

  /**
   * @param {SeasonRecordings} season
   * @returns {Promise<string>}
   * @private
   */
  _gridTitle(season: SeasonRecordings): Promise<string> {
    if (season.season) {
      return Promise.resolve(
        `${this.backend.translations.forKey(DetailTranslationKeys.detail_season)} ${season.season}`
      );
    }
    if (!this.recordings[0]?.series || !this.backend.deviceProfile?.profile.catalog) {
      return Promise.resolve('');
    }
    return EpgApiV5.series(this.backend, this.recordings[0].series)
      .then(series => {
        if (series) {
          return series.series.text.title || '';
        }
        return '';
      })
      .catch(err => {
        Log.warn('Error getting gridTitle, fallback to empty string', err);
        return '';
      });
  }

  handleDelete() {
    this.owner.fireAncestors(
      '$openDialog',
      confirmCancelDialog(
        '',
        this._deleteMessage(),
        this.deleteConfirmed.bind(this),
        undefined,
        this.backend.translations.forKey(PopupTranslationKeys.popup_btn_ok),
        this.backend.translations.forKey(PopupTranslationKeys.popup_btn_cancel)
      )
    );
  }

  forceDelete() {
    this.deleteConfirmed();
  }

  _deleteMessage() {
    if (this.folderInfo.pageType === RecordingPageTypes.recorded) {
      return this.backend.translations.forKey(RecordingTranslationKeys.recordings_delete_all_confirm);
    } else {
      return this.backend.translations.forKey(RecordingTranslationKeys.recordings_cancel_all_confirm);
    }
  }

  deleteConfirmed() {
    if (this.folderInfo.pageType === RecordingPageTypes.recorded) {
      this.backend.cachedRecordingsApi
        .deleteRecordings(this.backend, this.folderInfo.seriesId, this.folderInfo.channelId)
        .then(() => {
          this.returnToRecordingPageAndReload();
        });
    } else {
      this.backend.cachedRecordingsApi
        .cancelSeriesSubscription(this.backend, this.folderInfo.seriesId, this.folderInfo.channelId)
        .then(() => {
          this.returnToRecordingPageAndReload();
        });
    }
  }

  returnToRecordingPageAndReload() {
    this.owner.fireAncestors('$openPage', RecordingPage, {
      backend: this.backend,
      pageType: mapRecordingPageType(this.folderInfo.pageType),
    });
  }

  returnToPreviousPage() {
    this.owner.fireAncestors('$popFromStack');
  }
}
